import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChangeTaskText,
  ChangeTaskPriority,
  ChangeTaskDate,
  AddTask,
  ChangeTaskState,
  DeleteTask,
  DeleteAll,
  RestoreOriginalOrder,
  EditTask,
  SetSortType,
  SetSortOrder,
  SetFilterType
} from '../../Redux/TodoListSlice';
import Task from './Task/Task';
import { TextField, Button, Select, MenuItem, FormControl, InputLabel, Typography, Container, Grid, Box } from '@mui/material';
import './TodoList.css';

const TodoList = () => {
  const tasks = useSelector(state => state.tasks.tasks);
  const taskText = useSelector(state => state.tasks.taskText);
  const taskPriority = useSelector(state => state.tasks.taskPriority);
  const taskDate = useSelector(state => state.tasks.taskDate);
  const sortType = useSelector(state => state.tasks.sortType);
  const sortOrder = useSelector(state => state.tasks.sortOrder);
  const filterType = useSelector(state => state.tasks.filterType);
  const error = useSelector(state => state.tasks.error);
  const dispatch = useDispatch();

  const handleAddTask = () => {
    dispatch(AddTask());
  };

  const handleComplete = (id) => {
    dispatch(ChangeTaskState(id));
  };

  const handleDelete = (id) => {
    dispatch(DeleteTask(id));
  };

  const handleEdit = (id, newText, newPriority, newDate) => {
    dispatch(EditTask({ id, newText, newPriority, newDate }));
  };

  const handleSortOrder = () => {
    dispatch(SetSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'));
  };

  const getPriorityValue = (priority) => {
    switch (priority) {
      case 'low':
        return 1;
      case 'medium':
        return 2;
      case 'high':
        return 3;
      default:
        return 0;
    }
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    let comparison = 0;
    switch (sortType) {
      case 'name':
        comparison = a.text.localeCompare(b.text);
        break;
      case 'date':
        comparison = new Date(a.date).setHours(0, 0, 0, 0) - new Date(b.date).setHours(0, 0, 0, 0); // Сравниваем только даты без учета времени
        break;
      case 'priority':
        comparison = getPriorityValue(a.priority) - getPriorityValue(b.priority);
        break;
      case 'createdAt':
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
        break;
      default:
        break;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const filteredTasks = sortedTasks.filter(task => {
    switch (filterType) {
      case 'completed':
        return task.completed;
      case 'incomplete':
        return !task.completed;
      default:
        return true;
    }
  });

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddTask();
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom align="center">
        Todo List
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <TextField
                value={taskText}
                onChange={(e) => dispatch(ChangeTaskText(e.target.value))}
                label="Task name"
                variant="outlined"
                onKeyPress={handleKeyPress}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={taskPriority}
                onChange={(e) => dispatch(ChangeTaskPriority(e.target.value))}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="date"
              value={taskDate}
              onChange={(e) => dispatch(ChangeTaskDate(e.target.value))}
              label="Deadline"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button variant="contained" color="primary" onClick={handleAddTask} fullWidth>
              Add Task
            </Button>
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Typography color="error" align="center">{error}</Typography>
            </Grid>
          )}
        </Grid>
      </Box>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <Button variant="contained" onClick={() => dispatch(DeleteAll())} fullWidth>
            Delete All
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button variant="contained" onClick={() => dispatch(RestoreOriginalOrder())} fullWidth>
            Restore Original List
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button variant="contained" onClick={() => { dispatch(SetSortType('name')); handleSortOrder(); }} fullWidth>
            Sort by Name {sortOrder === 'asc' ? '↓' : '↑'}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button variant="contained" onClick={() => { dispatch(SetSortType('date')); handleSortOrder(); }} fullWidth>
            Sort by Deadline {sortOrder === 'asc' ? '↓' : '↑'}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button variant="contained" onClick={() => { dispatch(SetSortType('priority')); handleSortOrder(); }} fullWidth>
            Sort by Priority {sortOrder === 'asc' ? '↓' : '↑'}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button variant="contained" onClick={() => { dispatch(SetSortType('createdAt')); handleSortOrder(); }} fullWidth>
            Sort by Created At {sortOrder === 'asc' ? '↓' : '↑'}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Filter</InputLabel>
            <Select
              onChange={(e) => dispatch(SetFilterType(e.target.value))}
              value={filterType}
              label="Filter"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="incomplete">Incomplete</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Typography variant="body2" align="center" style={{ marginTop: 20 }}>
        Current Sort: {sortType} ({sortOrder})
      </Typography>
      <Grid container spacing={3} style={{ marginTop: 20 }}>
        {filteredTasks.map(task => (
          <Grid item xs={12} key={task.id}>
            <Task task={task} onComplete={handleComplete} onDelete={handleDelete} onEdit={handleEdit} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default TodoList;
